import axios, { type AxiosResponse } from 'axios';
import type { TUsuario } from '../types/usuarios';
import { isPublicPath } from '../utils/publicPaths';
import { novoCorrelationId } from '../utils/correlationId';
import { getFormFactor } from '../utils/formFactor';

export type { TUsuario };

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface LoginResponse {
  token: string;
  usuario: TUsuario;
  expiresAt: string;
}

export interface LoginRequest {
  loginOuEmail: string;
  senha: string;
  lembrarMe?: boolean;
}

export interface AlterarSenhaRequest {
  senhaAtual: string;
  novaSenha: string;
}

export interface AtualizarPerfilRequest {
  nome: string;
  email: string;
  fone?: string;
  endereco?: string;
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Correlation NOVO A CADA REQUEST: o backend adota este header como o
  // correlation da request, e é assim que um evento de auditoria e o erro
  // técnico da MESMA request se cruzam. Mandar o id de sessão colapsaria a
  // sessão inteira num id só (decisão 2026-08-19, spec §5).
  config.headers['X-Correlation-Id'] = novoCorrelationId();

  // Form factor pra coluna `origem` da telemetria de recursos. Sem ele, todo
  // uso vira 'web' e a pergunta "mobile é usado?" responde um "não" falso.
  config.headers['X-Client-Form-Factor'] = getFormFactor();

  return config;
});

// Sliding token renewal — se o backend devolver X-Refresh-Token, atualiza o
// auth_token no localStorage. Mantém usuários ativos sempre logados sem precisar
// re-autenticar enquanto estão usando a tela.
const aplicarRefreshToken = (headers?: Record<string, unknown>) => {
  if (!headers) return;
  const novo = headers['x-refresh-token'] ?? headers['X-Refresh-Token'];
  if (typeof novo === 'string' && novo.length > 0) {
    localStorage.setItem('auth_token', novo);
  }
};

// Handle 401 responses — dispara evento para o Router tratar, evita reload completo.
// Importante: NAO remove o auth_token aqui — o SessionExpiredDialog precisa do user
// atual (cache react-query) pra mostrar quem deve relogar. Token so eh removido se
// o usuario optar por sair pela tela de relogin.
api.interceptors.response.use(
  (response) => {
    aplicarRefreshToken(response.headers as Record<string, unknown>);
    return response;
  },
  (error) => {
    aplicarRefreshToken(error.response?.headers as Record<string, unknown> | undefined);
    // 401 e 403 originados de autenticacao (token expirado, invalido) abrem o
    // SessionExpiredDialog. 403 de autorizacao (sem permissao pra endpoint
    // especifico) NAO deveria abrir o dialog — o backend tem que ajudar a
    // distinguir (ex: header X-Auth-Failed ou body.reason). Por enquanto, so 401
    // abre o dialog; 403 deixa a chamada falhar localmente (toast no caller).
    // Pulamos a rota /login (onde o user ja esta logando) e rotas publicas.
    const status = error.response?.status;
    const path = window.location.pathname;
    const isLoginPage = path === '/login' || path.startsWith('/login');
    if (status === 401 && !isPublicPath(path) && !isLoginPage) {
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
    }
    // PLANO_UNIFICADO §6.4 — 402 Payment Required quando Hub indica Blocked.
    if (error.response?.status === 402) {
      const data = error.response.data as { message?: string; reason?: string; daysPastDue?: number } | undefined;
      window.dispatchEvent(new CustomEvent('access:blocked', { detail: data }));
    }
    return Promise.reject(error);
  }
);

// Auth API usando novo sistema PostgreSQL
export const authApi = {
  login: (data: LoginRequest): Promise<AxiosResponse<LoginResponse>> =>
    api.post('/usuarios/login', data),

  getProfile: (): Promise<AxiosResponse<TUsuario>> =>
    api.get('/usuarios/perfil'),
    
  updateProfile: (data: AtualizarPerfilRequest): Promise<AxiosResponse<TUsuario>> =>
    api.put('/usuarios/perfil', data),
    
  changePassword: (data: AlterarSenhaRequest): Promise<AxiosResponse<void>> =>
    api.post('/usuarios/alterar-senha', data),

  // Audit E-03 (2026-05-20) — revoga JWT atual no servidor (usuario_token_revogado).
  // Best-effort: front segue limpando localStorage mesmo se essa chamada falhar.
  logout: (): Promise<AxiosResponse<void>> =>
    api.post('/usuarios/logout'),
};

export default api;